import React, { useEffect, useState } from 'react';
import { useSites } from '../../../contexts/SitesContext';
import Input from '../../../shared/forms/Input';
import Modal, { ModalCloseButton } from '../../../shared/modals/Modal';
import TooltipTrigger from '../../../shared/components/TooltipTrigger';
import TrackingCodeSection from '../../../shared/components/TrackingCodeSection';
import {
  normalizeSiteDomain,
  SITE_DOMAIN_HINT,
  validateSiteDomain,
} from './siteDomainValidation';

const AddSiteModal = ({ setAddSiteModalVisible, existingNames }) => {
  const { addSite } = useSites();
  const [domain, setDomain] = useState('');
  const [message, setMessage] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const [showDomainAttention, setShowDomainAttention] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!showDomainAttention) return undefined;

    const timeoutId = window.setTimeout(() => {
      setShowDomainAttention(false);
    }, 900);

    return () => window.clearTimeout(timeoutId);
  }, [showDomainAttention]);

  const triggerDomainAttention = () => {
    setShowDomainAttention(false);
    window.requestAnimationFrame(() => {
      setShowDomainAttention(true);
    });
  };

  const closeModal = () => setAddSiteModalVisible(false);

  return (
    <Modal isOpen={true} onClose={closeModal} className="modal--medium modal--full-height">
      <div className="modal">
        <div className="modal__header">
          <h3 className="margin-none">Add a website</h3>
          <ModalCloseButton
            onClick={closeModal}
            label="Close add website modal"
          />
        </div>

        <div className="modal__body">
          <form
            id="add-site-form"
            onSubmit={(e) => {
              e.preventDefault();

              if (disabled || submitting) {
                triggerDomainAttention();
                return;
              }

              setSubmitting(true);

              addSite(normalizeSiteDomain(domain)).then((result) => {
                if (result.success) {
                  closeModal();
                  return;
                }

                setSubmitting(false);
                setMessage(result.message || 'Something went wrong. Please try again.');
              });
            }}
          >
            <div className="modal-section">
              <h4 className="modal-section__title">Site information</h4>

              <div className="field-stack">
                <Input
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    const nextValidation = validateSiteDomain({
                      value: nextValue,
                      existingNames,
                    });

                    setDomain(nextValue);
                    setMessage(nextValidation.message);
                    setDisabled(Boolean(nextValidation.message));
                  }}
                  label="Domain name"
                  value={domain}
                  placeholder="example.com"
                  className={showDomainAttention ? 'is-attention' : ''}
                  icon={<TooltipTrigger text={SITE_DOMAIN_HINT} />}
                />
                {message && (
                  <p className="field-message">
                    {message}
                  </p>
                )}
              </div>
            </div>

            <TrackingCodeSection />
          </form>
        </div>

        <div className="modal__footer">
          <button
            type="button"
            className="btn secondary"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-site-form"
            className={'btn primary' + (disabled || submitting ? ' disabled' : '')}
            aria-disabled={disabled || submitting}
            onClick={() => {
              if (disabled) {
                triggerDomainAttention();
              }
            }}
          >
            {submitting ? 'Adding…' : 'Add website'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddSiteModal;
